import {
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PencilSquareIcon,
} from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";

const STATE_CONFIG = {
  editing: {
    Icon: PencilSquareIcon,
    label: "Editing",
    textClassName: "text-amber-700",
  },
  saving: {
    Icon: ArrowPathIcon,
    iconClassName: "animate-spin",
    label: "Saving…",
    textClassName: "text-gray-600",
  },
  saved: {
    Icon: CheckCircleIcon,
    label: "Saved",
    textClassName: "text-green-700",
  },
  error: {
    Icon: ExclamationTriangleIcon,
    label: "Save failed",
    textClassName: "text-red-600",
  },
};

const FormSaveStatus = ({
  fadeDuration = 1500,
  savedHoldDuration = 3000,
  state,
}) => {
  const [displayedState, setDisplayedState] = useState(state);
  const [opacity, setOpacity] = useState(1);
  const displayedStateRef = useRef(state);
  const savedHoldTimerRef = useRef();
  const fadeTimerRef = useRef();

  useEffect(() => {
    clearTimeout(savedHoldTimerRef.current);
    clearTimeout(fadeTimerRef.current);

    if (state !== "idle") {
      displayedStateRef.current = state;
      setDisplayedState(state);
      setOpacity(1);
    } else if (displayedStateRef.current === "saved") {
      savedHoldTimerRef.current = setTimeout(() => {
        setOpacity(0);
        fadeTimerRef.current = setTimeout(() => {
          displayedStateRef.current = "idle";
          setDisplayedState("idle");
        }, fadeDuration);
      }, savedHoldDuration);
    } else {
      displayedStateRef.current = "idle";
      setDisplayedState("idle");
      setOpacity(1);
    }

    return () => {
      clearTimeout(savedHoldTimerRef.current);
      clearTimeout(fadeTimerRef.current);
    };
  }, [fadeDuration, savedHoldDuration, state]);

  const config = STATE_CONFIG[displayedState];

  if (!config) {
    return <div aria-hidden="true" className="mr-3 w-14 shrink-0" />;
  }

  const { Icon, iconClassName = "", label, textClassName } = config;

  return (
    <div
      aria-atomic="true"
      aria-live={displayedState === "error" ? "assertive" : "polite"}
      className={`mr-3 grid w-14 shrink-0 grid-cols-[1rem_1fr] items-center gap-1.5 whitespace-nowrap text-left text-xs font-medium ${textClassName}`}
      role="status"
      style={{
        opacity,
        transition: `opacity ${fadeDuration}ms linear`,
        willChange: "opacity",
      }}
    >
      <Icon className={`h-4 w-4 stroke-2 ${iconClassName}`} />
      <span>{label}</span>
    </div>
  );
};

export { FormSaveStatus };
